import { useRouter } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

/** GA4 measurement ID provided by the site owner. */
export const GA_MEASUREMENT_ID = "G-ZGWT9DCYEM";

type GtagWindow = Window & {
  gtag?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
  __amigoGtagLoading?: boolean;
};

function getAnalyticsWindow(): GtagWindow | null {
  if (typeof window === "undefined") return null;
  return window as GtagWindow;
}

/** Ensure the dataLayer/gtag stub exists before the remote script arrives. */
function ensureGtagStub(w: GtagWindow) {
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag === "function") return;

  w.gtag = function gtag(...args: unknown[]) {
    w.dataLayer!.push(args);
  };
}

/**
 * Load gtag.js after first paint — on idle, first interaction, or a short timeout.
 * Keeps ~160 KiB of Tag Manager off the critical path and out of Lighthouse unused-JS.
 */
export function loadGoogleAnalytics() {
  const w = getAnalyticsWindow();
  if (!w || w.__amigoGtagLoading) return;
  w.__amigoGtagLoading = true;

  ensureGtagStub(w);
  w.gtag!("js", new Date());
  w.gtag!("config", GA_MEASUREMENT_ID);

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);
}

function scheduleGoogleAnalytics() {
  const w = getAnalyticsWindow();
  if (!w || w.__amigoGtagLoading) return;

  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    cleanup();
    loadGoogleAnalytics();
  };

  const onInteract = () => run();
  const events = ["pointerdown", "keydown", "touchstart", "scroll"] as const;
  const cleanup = () => {
    events.forEach((event) => window.removeEventListener(event, onInteract, { capture: true }));
  };

  events.forEach((event) =>
    window.addEventListener(event, onInteract, { once: true, passive: true, capture: true }),
  );

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 3500);
  }

}

/**
 * Fire non-personal analytics events when a tracker is present.
 * Never include names, emails, phone numbers, or message content.
 */
export function trackEvent(name: string, params?: Record<string, string | number | boolean>) {
  const w = getAnalyticsWindow();
  if (!w) return;

  const payload = params ? { ...params } : undefined;

  if (typeof w.gtag === "function") {
    w.gtag("event", name, payload);
    return;
  }

  if (Array.isArray(w.dataLayer)) {
    w.dataLayer.push({ event: name, ...payload });
  }
}

/** Track client-side route changes as GA4 page views; defer gtag load until idle. */
export function useAnalytics() {
  const router = useRouter();
  const { pathname, searchStr } = router.state.location;
  const isFirst = useRef(true);

  useEffect(() => {
    scheduleGoogleAnalytics();
  }, []);

  useEffect(() => {
    // gtag('config') records the landing page_view once the script loads.
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }

    const w = getAnalyticsWindow();
    if (!w || typeof w.gtag !== "function") return;

    w.gtag("event", "page_view", {
      page_path: pathname + (searchStr ?? ""),
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, searchStr]);
}
